<template>
  <v-container>
    <div class="title-icon top">
      <h1>{{ $t('company.exemption-title.exemption') }}</h1>
      <v-btn
        icon
        :href="links.application.instructorDispensations"
        target="_blank"
      >
        <v-icon>$sl-question</v-icon>
      </v-btn>
    </div>

    <p>{{ $t('company.exemption-p1') }}</p>
    <p>{{ $t('company.exemption-p2') }}</p>
    <v-form ref="form" v-model="valid" lazy-validation>
      <v-select
        outlined
        v-model="employee"
        :items="employees"
        :rules="[rules.required]"
        item-text="text"
        item-value="personId"
        :label="$t('labels.label-select-employee')"
        :placeholder="$t('labels.placeholder-select-employee')"
        append-icon="$sl-arrow-down"
        return-object
      ></v-select>
      <v-select
        outlined
        append-icon="$sl-arrow-down"
        v-model="dispensType"
        :items="dispensTypes"
        :label="$t('labels.label-select-exemption-type')"
      ></v-select>
      <v-select
        outlined
        v-model="profession"
        :items="professions"
        :rules="[rules.required]"
        item-text="text"
        item-value="id"
        :label="$t('labels.placeholder-select-profession')"
        :placeholder="$t('labels.placeholder-select-profession')"
        append-icon="$sl-arrow-down"
        return-object
      ></v-select>

      <v-select
        v-if="isMachineProfession(profession.id)"
        outlined
        multiple
        v-model="machineIds"
        :items="machineOrientations(profession.id)"
        :rules="[rules.notEmpty]"
        item-text="text"
        item-value="id"
        :label="$t('labels.placeholder-select-machine-orientation')"
        :placeholder="$t('labels.placeholder-select-machine-orientation')"
        append-icon="$sl-arrow-down"
      ></v-select>

      <v-textarea
        :label="$t('labels.label-reason')"
        :rules="[rules.required]"
        outlined
        name="input-7-4"
        rows="10"
        :placeholder="$t('labels.placeholder-text-area-exemption-motive')"
        v-model="exemptionReason"
      ></v-textarea>
    </v-form>
    <p class="small mb-8">{{ $t('company.exemption-text') }}</p>
    <div v-if="selectedFiles && selectedFiles.length > 0">
      <div v-for="(file, index) in selectedFiles" :key="index">
        <v-text-field
          :value="file.name"
          prepend-icon=""
          readonly
          accept=".pdf, .jpg, .tiff, .png"
          append-outer-icon="$sl-delete"
          class="mt-n4"
          outlined
          :label="$t('labels.label-upload-file')"
          @click:append-outer="showDeleteModal(file)"
        ></v-text-field>
      </div>
    </div>
    <v-text-field
      class="mt-n5 font-italic"
      :placeholder="$t('application.documents.loading')"
      v-if="isLoading"
      outlined
      append-outer-icon="$sl-spinner"
    ></v-text-field>
    <v-form ref="uploadForm" v-model="valid1" lazy-validation>
      <v-file-input
        class="mt-n5 d-none"
        ref="inputUpload1"
        accept=".pdf, .jpg, .tiff, .png"
        v-model="files"
        @change="saveFiles"
      ></v-file-input>
    </v-form>
    <ActionButton
      @click="$refs.inputUpload1.$refs.input.click()"
      type="application"
      class="mb-12 mt-n3"
      actionIcon="$sl-attachment"
      :isDisabled="isImitatingUser() || isImitatingUser()"
      >{{ $t('labels.upload-file') }}</ActionButton
    >

    <ActionButton @click="saveExemption" actionIcon="$sl-single-neutral-actions-check" :isDisabled="isImitatingUser()">{{
      $t('labels.new-exemption')
    }}</ActionButton>

    <ModalBase
        :show="showModal"
        maxWidth="400px"
        :title="$t('application.documents.delete-file')"
        cancelIcon="$sl-cancel"
        submitIcon="$sl-delete"
        cancelText="Avbryt"
        submitText="Radera"
        @close="closeModal"
        @cancel="closeModal"
        @submit="deleteFile"
        :outlinedSubmit="true"
    />
  </v-container>
</template>

<script>
import API from '@/services/api.js'
import {RulesMixin} from '@/components/mixins'
import ActionButton from '@/components/Common/ActionButton'
import { mapGetters } from 'vuex'
import ModalBase from '@/components/Common/ModalBase';
import ModalBaseMixin from "@/components/mixins/ModalBaseMixin";
import flatten from 'lodash/flatten'

// @group Company
// CompanyExemption component
export default {
  name: 'CompanyExemption',

  mixins: [RulesMixin, ModalBaseMixin],

  components: {
    ActionButton,
    ModalBase
  },

  data: () => ({
    links: require('../../lib/external-links.json'),
    search: '',
    addNewFile: false,
    valid: true,
    tab: null,
    dispensType: 'Handledare',
    dispensTypes: [{text: 'Handledare'}],
    files: {},
    selectedFiles: [],
    employees: [],
    employee: {},
    professions: [],
    profession: {},
    machineIds: [],
    valid: true,
    valid1: true,
    exemptionId: null,
    exemptionReason: '',
    isLoading: false
  }),

  mounted() {
    this.$scrollToTop()
  },

  created() {
    this.init()
  },

  computed: {
    ...mapGetters('basic', ['basicProfessions']),
    ...mapGetters('currentuser', ['currentUser', 'isImitatingUser']),

    machineOrientations() {
      return professionId => flatten(this.basicProfessions.map(profession => {
        return profession.machineOrientations.map(orientation => {
          if(orientation.professionId === professionId) {
            return { id: orientation.machineId, text: orientation.name }
          }
        }).filter(o => o)
      }))
    },

    isMachineProfession() {
      return professionId => !!this.basicProfessions.some(o => o.id === professionId && o.isMachineProfession)
    }
  },

  methods: {
    /**
     * @vuese
     * Used to call method  getApprenticeById or  getEmployeeById based on route name
     * @arg called with no argument
     */
    init() {
      this.getProfessions()
      this.getEmployees()
    },

    /**
     * @vuese
     * Used to fetch professions and get the selected profession if there is any
     */
    getProfessions() {
      this.professions = this.basicProfessions.map(o => {
        return {id: o.id, text: o.name}
      })
    },
    /**
     * @vuese
     * Used to fetch employees
     * @arg The argument is an integer value representing company id
     */
    getEmployees() {
      API.listEmployments(this.$route.params.company_id)
        .then(response => {
          let textName = ''
          response.data.forEach(o => {
            textName = ''
            if (o.employed.firstName && o.employed.lastName) {
              textName = o.employed.firstName + ' ' + o.employed.lastName
            } else if (o.employed.email) {
              textName = o.employed.email
            }
            this.employees.push({
              employee_id: o.id,
              personId: o.employed.id,
              text: textName
            })
          })

          if (this.$route && this.$route.params && this.$route.params.employee_id) {
            this.getEmployeeById()
          }
        })
        .catch(error => {
          console.log('catch Error')
        })
    },
    getEmployeeById() {
      API.getEmployee(this.$route.params.employee_id)
        .then(response => {
          this.employee = {
            employee_id: response.data.id,
            personId: response.data.employed.id,
            firstName: response.data.employed.firstName,
            lastName: response.data.employed.lastName
          }
        })
        .catch(error => {
          console.log('There is no employee for this id- ' + error)
        })
    },
    /**
     * @vuese
     * Used to push a file to array an display it
     * @arg The argument is an object representing file
     */
    saveFiles(file) {
      if (!file) {
        return
      }
      if (!this.$refs.uploadForm.validate()) {
        this.$events.fire('showError', this.$t('errors.save-document-size'))
        return
      }
      this.isLoading = true
      //used only to display
      this.selectedFiles.push(file)
      this.files = {}
      this.addNewFile = false
      this.isLoading = false
    },
    /**
     * @vuese
     * Used to delete a file from files array
     * @arg The argument is an integer value representing index
     */
    async deleteFile() {
      this.showModal = false;

      this.selectedFiles.splice(this.selectedFiles.indexOf(this.selectedItem), 1)
      this.selectedFiles = [...this.selectedFiles]
      this.files = {}
      this.$refs.uploadForm.reset()
    },

    /**
     * @vuese
     * Used to save all the files selected  and create a new exemption
     * @arg The argument is an array of files
     */
    saveExemption() {

      if (!this.$refs.form.validate()) {
        this.$events.fire('showError', this.$t('errors.form-validation'))
        return
      }
      let promises = []
      this.selectedFiles.forEach(file => {
        let fileData = new Promise((resolve, reject) => {
          let reader = new FileReader()
          reader.onload = () => {
            resolve({data: reader.result, name: file.name, size: file.size})
          }
          reader.onerror = reject
          reader.readAsDataURL(file)
        })
        promises.push(fileData)
      })

      Promise.all(promises)
        .then(dataUrls => {
          let prepareDataUrls = []
          dataUrls.forEach(dataUrl => {
            prepareDataUrls.push({data: dataUrl.data, name: dataUrl.name, size: dataUrl.size})
          })

          let createData = {
            companyId: this.$route.params.company_id,
            professionId: this.profession.id,
            personId: this.employee.personId,
            exemptionReason: this.exemptionReason,
            addDocuments: prepareDataUrls,
            machineIds: this.machineIds
          }
          API.createExemption(this.$route.params.company_id, {data: createData})
            .then(response => {
              if (response && response.data) {
                this.$nextTick(() => {
                  this.$router.push({
                    name: 'CompanyExemptionThankYou',
                    params: {employee_id: this.employee.employee_id}
                  })
                })
              }
              this.isLoading = false
            })
            .catch(error => {
              console.log('catch Error', error)
              if (error && error.response && error.response.status == 400) {
                this.$events.fire('showError', this.$t('errors.file-type-error'))
              }
              this.isLoading = false
            })
        })
        .catch(err => {
          console.log(err)
        })
    },
    redirectToManuall() {
      window.location.href = 'https://www.byggbranschensyrkesnamnd.se/manual/dispenser'
    }
  }
}
</script>
<style >
</style>
